import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';

import bgImage from '../../images/landingPage_916x964.png';
import getTokenDetails from '../../auth/getTokenDetails';

import {
  fadeTimingMs,
  defaultNoAuthRoute,
  landingPageRoute,
  defaultNoAccountRoute,
  growExecutiveRoute,
  farmsHomeRoute,
  accountAppLabel,
  dashboardModelName,
  crudActions,
  landingPageClasses,
  getDarkThemeClass,
  contactUsRoute,
} from '../utility/constants';

const headline = 'More for the Grow.';
const description =
  'SmartGrow is a SaaS platform powered by big data designed to help businesses maximize margins and profits through their cannabis cultivation operations.';

export default function LandingPage(props) {
  const theme = useTheme();

  const navigate = useNavigate();

  // 1

  const currDarkThemeClass = useMemo(() => getDarkThemeClass(theme), [theme?.palette?.themeMode]);

  const { token, tokenDetails } = getTokenDetails();

  const { permissions = [], is_staff = false } = tokenDetails || '';

  const growDashPermissions = useMemo(() => permissions?.filter(perm => perm.includes(`_${dashboardModelName}`)) ?? [], [token]);

  const growDashPerms = Object.fromEntries(
    Object.keys(crudActions).map(action => [
      action,
      is_staff ? true : growDashPermissions?.includes(`${accountAppLabel}.${crudActions?.[action]}_${dashboardModelName}`) ?? false,
    ])
  );

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate(landingPageRoute);
  };

  const lPBoxProps = {
    className: `${landingPageClasses?.box} ${currDarkThemeClass}`,
  };

  const lPNavBoxProps = {
    className: `${landingPageClasses?.navBox} ${currDarkThemeClass}`,
  };

  const lPContentBoxProps = {
    className: `${landingPageClasses?.contentBox} ${currDarkThemeClass}`,
  };

  const lPNavTitleTypoProps = {
    noWrap: true,
    className: `${landingPageClasses?.navTitleTypo} ${currDarkThemeClass}`,
  };

  const lPNavAccessBtnProps = ({ otherSxProps = {}, login, logout, otherNavBtnClass = '' }) => ({
    // className: landingPageClasses?.navAccessBtn,
    className: `${landingPageClasses?.navAccessBtn} ${currDarkThemeClass} ${otherNavBtnClass}`,
    onClick: () => (logout ? handleLogout() : navigate(login ? defaultNoAuthRoute : defaultNoAccountRoute)),
    sx: {
      ...otherSxProps,
    },
    variant: 'contained',
  });

  const lPNavAccessGrowAssistBtnProps = ({ otherSxProps = {}, otherNavBtnClass = '' }) => ({
    // className: landingPageClasses?.navAccessBtn,
    className: `${landingPageClasses?.navAccessBtn} ${currDarkThemeClass} ${landingPageClasses?.loginBtn}`,
    onClick: () => {
      // Previous code - opens in new window
      // window.open('http://app.smartgrow.farm/grow-assist/MFzqkoAnyUCE7BTa', '_blank');

      // New code - redirects in same window
      // navigate('/grow-assistance');
      window.open('/grow-assistance','_blank')

    },
    sx: {
      ...otherSxProps,
    },
    variant: 'contained',
  });

  // 2

  const lPHeadlineTypoProps = {
    className: `${landingPageClasses?.headlineTypo} ${currDarkThemeClass}`,
  };

  const lPDescTypoProps = {
    className: `${landingPageClasses?.descTypo} ${currDarkThemeClass}`,
  };

  const lPAccessBtnProps = {
    className: `${landingPageClasses?.accessBtn} ${currDarkThemeClass}`,
    onClick: () =>
      localStorage.token ? (growDashPerms?.read ? navigate(growExecutiveRoute) : navigate(farmsHomeRoute)) : navigate(defaultNoAccountRoute),
    variant: 'contained',
  };
  const lContactBtnProps = {
    className: `${landingPageClasses?.accessBtn} ${currDarkThemeClass}`,
    onClick: () => navigate(contactUsRoute),
    variant: 'contained',
  };

  const lPBgImageBoxProps = {
    className: `${landingPageClasses?.bgImageBox} ${currDarkThemeClass}`,
  };

  const loginProps = {
    login: true,
    otherNavBtnClass: landingPageClasses?.loginBtn,
  };

  const logoutProps = {
    logout: true,
  };

  return (
    <Box {...lPBoxProps}>
      <Box {...lPNavBoxProps}>
        <Typography {...lPNavTitleTypoProps}>SMARTGROW</Typography>
        {!localStorage.token ? (
          <>
            <Button {...lPNavAccessBtnProps(loginProps)}>Login</Button>
            <Button {...lPNavAccessBtnProps({})}>Request Access</Button>
          </>
        ) : (
          <>
            <Button {...lPNavAccessGrowAssistBtnProps({})}>Grow Assist</Button>
            <Button {...lPNavAccessBtnProps(logoutProps)}>Logout</Button>
          </>
        )}
      </Box>

      <Fade in={true} timeout={fadeTimingMs}>
        <Box {...lPContentBoxProps}>
          <Typography {...lPHeadlineTypoProps}>{headline}</Typography>
          <Typography {...lPDescTypoProps}>{description}</Typography>
          <Box display="flex">
            <Button {...lPAccessBtnProps}>{localStorage.token ? 'Application' : 'Request Access'}</Button>
            <Button {...lContactBtnProps}>Contact Us</Button>
          </Box>
          <Fade in={true} timeout={fadeTimingMs}>
            <Box {...lPBgImageBoxProps}>
              <img src={bgImage} alt="Smartgrow Dashboard" />
            </Box>
          </Fade>
        </Box>
      </Fade>
    </Box>
  );
}
